<template>
  <div class="flex items-center gap-2 w-full flex-wrap" draggable="true">
    <div class="flex-1 min-w-[90px] max-w-[90px]">
      <span v-if="isFirst" class="display: block; width: 100%; max-width: 90px; text-typography-primary">
        {{ t('where') }}
      </span>
      <div v-else>
        <PulseDropdown
          v-model:shown="isLogicalOperatorDropdownOpen"
          :searchable="false"
          :model-value="localFilter.logicalOperator"
          :items="logicalOperatorOptions.map(option => ({ label: option.value.toUpperCase(), value: option.value }))"
          :placeholder="t('placeholders.add')"
          @update:model-value="handleLogicalOperatorChange"
        />
      </div>
    </div>

    <div class="flex-1 min-w-[150px]">
      <PulseDropdown
        v-model:model-value="localFilter.field"
        v-model:shown="isFieldDropdownOpen"
        :items="fieldOptions.map(option => ({ label: option.label, value: option.field }))"
        :placeholder="t('placeholders.select')"
        @update:model-value="handleFieldChange"
      />
    </div>

    <div class="flex-1 min-w-[50px] max-w-[100px]">
      <PulseDropdown
        v-model:model-value="localFilter.operator"
        v-model:shown="isOperatorDropdownOpen"
        dropdown-width="150px"
        :items="conditionOptions.map(option => ({ label: option.label, value: option.value }))"
        :placeholder="t('placeholders.select')"
        @update:model-value="handleOperatorChange"
      />
    </div>

    <div class="flex-1 min-w-[150px]">
      <template v-if="localFilter.operator !== 'null' && localFilter.operator !== 'NOT_null'">
        <PulseDropdown
          v-if="valueOptions.length > 1"
          v-model:model-value="localFilter.value"
          v-model:shown="isValueDropdownOpen"
          :items="valueOptions.map(option => ({ label: option.label, value: option.value }))"
          :placeholder="t('placeholders.select')"
          @update:model-value="updateFilter"
        />

        <input
          v-else-if="['date', 'datetime'].includes((selectedFieldConfig?.type || '').toLowerCase())"
          v-model="dateInputString"
          type="date"
          :class="inputClasses"
        />

        <input
          v-else-if="['int', 'float'].includes((selectedFieldConfig?.type || '').toLowerCase())"
          v-model="localFilter.value"
          type="number"
          :class="inputClasses"
          :placeholder="t('placeholders.add')"
          @input="updateFilter"
        />

        <input
          v-else
          v-model="localFilter.value"
          type="text"
          :class="inputClasses"
          :placeholder="t('placeholders.add')"
          @input="updateFilter"
        />
      </template>
    </div>

    <div class="flex flex-none items-center gap-1">
      <PulseIconButton
        icon="LineTrashAlt"
        variant="secondary"
        size="large"
        @click="$emit('deleteFilter', props.index, $event)"
      />
      <PulseIconButton
        icon="LineDraggabledots"
        variant="secondary"
        size="large"
        class="cursor-move"
        @mousedown="$emit('startDrag', props.index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import type { FilterQuery, FilterConfig } from '@monolog/shared/types/filter.types';
import { useI18n } from 'vue-i18n';
import { LogicalOperator } from '@constants';
import { isValid, parseISO, format } from 'date-fns';

const isFieldDropdownOpen = ref(false);
const isOperatorDropdownOpen = ref(false);
const isValueDropdownOpen = ref(false);

const emit = defineEmits<{
  (event: 'updateFilter', index: number, filter: FilterQuery): void;
  (event: 'updateLogicalOperator', index: number, value: string): void;
  (event: 'deleteFilter', index: number, e: MouseEvent): void;
  (event: 'startDrag', index: number): void;
}>();

const props = defineProps<{
  selectedFilter: FilterQuery;
  fieldOptions: FilterConfig[];
  isFirst: boolean;
  index: number;
  allSelectedFields: string[];
  availableLogicalOperators: LogicalOperator[];
}>();

const { t } = useI18n({ useScope: 'local' });

const logicalOperatorOptions = computed(() => props.availableLogicalOperators.map(operator => ({ value: operator })));

const allLogicalOperators = [LogicalOperator.AND, LogicalOperator.OR];

const isLogicalOperatorDropdownOpen = ref(false);
const localFilter = ref(props.selectedFilter);

const inputClasses = computed(() => {
  return 'h-[44px] w-full px-4 focus:outline-primary-700 border border-neutral-300 text-typography-primary rounded-md whitespace-nowrap overflow-hidden text-ellipsis bg-neutral-white';
});

const selectedFieldConfig = computed(() => {
  return props.fieldOptions.find(f => f.field === localFilter.value.field) || null;
});

const conditionOptions = computed(() => selectedFieldConfig.value?.operators || []);
const valueOptions = computed(() => selectedFieldConfig.value?.values || []);

const dateInputString = computed({
  get() {
    const val = localFilter.value.value;

    if (val instanceof Date && isValid(val)) {
      return format(val, 'yyyy-MM-dd');
    }

    if (typeof val === 'string') {
      const parsed = parseISO(val);
      if (isValid(parsed)) return format(parsed, 'yyyy-MM-dd');
    }

    return '';
  },
  set(newDateStr: string) {
    const [year, month, day] = newDateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day, 0, 0, 0);

    if (isValid(date)) {
      localFilter.value.value = date;
      updateFilter();
    }
  },
});

function handleLogicalOperatorChange(selectedLabel: string) {
  const selectedOption = allLogicalOperators.find(option => option === selectedLabel);
  if (selectedOption && !props.isFirst) {
    localFilter.value.logicalOperator = selectedOption;
  }
  updateFilter();
}

function handleOperatorChange(selectedLabel: string) {
  const selectedOption = conditionOptions.value.find(option => option.label === selectedLabel);
  if (selectedOption) {
    localFilter.value.operator = selectedOption.value;
    if (['null', 'NOT_null'].includes(selectedOption.value)) {
      localFilter.value.value = undefined;
    }
    updateFilter();
  }
}

function handleFieldChange(selectedField: string) {
  const selectedConfig = props.fieldOptions.find(option => option.field === selectedField);
  if (selectedConfig) {
    localFilter.value.field = selectedConfig.field;
    localFilter.value.operator = selectedConfig.operators[0]?.value || '';
    localFilter.value.type = selectedConfig.type;
    localFilter.value.value = undefined;
    localFilter.value.logicalOperator = props.isFirst
      ? undefined
      : props.selectedFilter.logicalOperator || props.availableLogicalOperators[0];
  }
  updateFilter();
}

function toFilterQuery(filter: {
  field: string;
  operator: string;
  value?: any;
  type: string;
  logicalOperator?: string;
}): FilterQuery {
  return {
    field: filter.field,
    logicalOperator: filter.logicalOperator,
    operator: filter.operator,
    type: filter.type,
    value: filter.value,
  };
}

function updateFilter() {
  if (localFilter.value.field && localFilter.value.operator) {
    const filterType = localFilter.value.type || 'defaultType';
    emit(
      'updateFilter',
      props.index,
      toFilterQuery({
        ...localFilter.value,
        type: filterType,
      }),
    );
  }
}

watch(
  () => props.selectedFilter,
  newVal => {
    localFilter.value = { ...newVal };
  },
  { deep: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "placeholders": {
      "select": "Select",
      "add": "Add"
    },
    "where": "Where"
  }
}
</i18n>
